import {
  ArrowDownToSquare,
  ArrowRotateLeft,
  ArrowRotateRight,
  CircleStop,
  Copy,
  SquareListUl,
  TrashBin,
} from "@gravity-ui/icons";
import { Alert, DropdownMenu, Icon } from "@gravity-ui/uikit";
import { omit } from "@reatom/framework";
import { useAction } from "@reatom/npm-react";
import { CellContext } from "@tanstack/react-table";
import { StartExportTaskRequestDto } from "definitions/exporter";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  cancelExportJobRequest,
  deleteRepeatableJobRequest,
  getExportJobsRequest,
  JobsWithChildren,
  retryExportJobRequest,
  startExportTaskRequest,
} from "src/pages/ExportPage/store/fetchers";
import {
  exportLogsModalAtom,
  selectJobAtom,
} from "src/pages/ExportPage/widgets/ResponsiveExportLogsWidget/store/atoms";
import { toastHandler } from "src/util";
import { ShowConfirmDialog } from "src/widgets/Alert/Alert";

interface JobsTableActionsProps {
  info: CellContext<JobsWithChildren, unknown>;
}

export const JobsTableActions = ({ info }: JobsTableActionsProps) => {
  const getExportJobs = useAction(getExportJobsRequest);
  const retryJob = useAction(retryExportJobRequest);
  const cancelJob = useAction(cancelExportJobRequest);
  const deleteRepeatableJob = useAction(deleteRepeatableJobRequest);
  const toggleOpenLogs = useAction(exportLogsModalAtom.toggle);
  const setSelectedJob = useAction(selectJobAtom);
  const startExportTask = useAction(startExportTaskRequest);

  const { taskId } = useParams();

  const { id, status, resultLink, data, statuses } = info.row.original;

  const onRetryWarningProducts = useCallback(async () => {
    if (!data || !taskId || !statuses?.warnings?.ids?.length) return;

    const jobData: StartExportTaskRequestDto = omit(
      {
        ...data,
        id: taskId,
        filename: `${data.filename}_${new Date().getTime()}`,
        productIds: statuses.warnings.ids,
      },
      ["selectProductSampleId", "counter", "taskId", "userId"],
    );

    await startExportTask(jobData);
    await getExportJobs({ id: taskId });
  }, [taskId]);

  const handleOnCancleJob = useCallback(async () => {
    if (!taskId || !id) return;
    const result = await ShowConfirmDialog(
      <Alert
        theme="warning"
        title="Если вы прервете задачу, то запросы за уже полученные товары не вернутся."
        message="Вы действительно хотите прервать задачу?"
      />,
      "Подтверждение",
    );

    if (!result) return;

    await cancelJob({ id });
    await getExportJobs({ id: taskId });
  }, [cancelJob, getExportJobs, id, taskId]);

  const handleOnDeleteJob = useCallback(async () => {
    if (!taskId || !id) return;
    await deleteRepeatableJob({ id });
    await getExportJobs({ id: taskId });
  }, [deleteRepeatableJob, getExportJobs, id, taskId]);

  const handleOnRetryJob = useCallback(async () => {
    if (!taskId || !id) return;
    await retryJob({ id });
    await getExportJobs({ id: taskId });
  }, [retryJob, getExportJobs, id, taskId]);

  const isRepeat =
    info.row.getCanExpand() || status === "delayed" || status === "stopped";
  const isRepeatStopped = status === "stopped";

  const onCopyResultLink = async () => {
    await navigator.clipboard.writeText(resultLink);
    toastHandler.success({
      title: "Ссылка скопирована!",
      content: `${resultLink.slice(0, 32)}...`,
      name: "copyExportJobResultLink",
    });
  };

  const onToggleLogsOpen = useCallback(() => {
    const job = info.row.original;
    setSelectedJob(job);
    toggleOpenLogs();
  }, [info.row.original, setSelectedJob, toggleOpenLogs]);

  const isDisabledStatus = useMemo(
    () => ["completed", "failed", "delayed", "stopped"].includes(status),
    [status],
  );

  return (
    <DropdownMenu
      items={[
        {
          iconStart: <Icon data={Copy} size={16} />,
          action: onCopyResultLink,
          disabled: status !== "completed",
          text: "Скопировать ссылку",
        },
        {
          iconStart: <Icon data={ArrowDownToSquare} size={16} />,
          action: () => {
            window.open(resultLink, "_blank");
          },
          disabled: status !== "completed",
          text: "Скачать выгрузку",
        },
        {
          iconStart: <Icon data={CircleStop} size={16} />,
          action: handleOnCancleJob,
          disabled: isDisabledStatus,
          text: "Прервать задачу",
        },
        {
          iconStart: <Icon data={ArrowRotateLeft} size={16} />,
          action: onRetryWarningProducts,
          disabled: !statuses?.warnings?.ids?.length || isRepeat,
          text: "Повторить не успешные товары",
        },
        {
          iconStart: <Icon data={ArrowRotateRight} size={16} />,
          action: handleOnRetryJob,
          disabled: !isDisabledStatus || isRepeat,
          text: "Перезапустить задачу",
        },
        {
          iconStart: <Icon data={TrashBin} size={16} />,
          action: handleOnDeleteJob,
          disabled: isRepeatStopped || !isRepeat,
          text: "Удалить задачу",
        },
        {
          iconStart: <Icon data={SquareListUl} size={16} />,
          action: onToggleLogsOpen,
          disabled: !isDisabledStatus,
          text: "Посмотреть логи",
        },
      ]}
    />
  );
};
